import type { ColDef, IRowNode, ValueGetterParams } from "ag-grid-community";

import type { ReviewDetailsFrontend } from "@performyard/service-contracts";

import {
	ActionCellRenderer,
	AdminsOnlyCellRenderer,
	FormCountCellRenderer,
	FormCountWithAlertCellRenderer,
	SubjectGroupCellRenderer,
	TotalProgressCellRenderer,
	TurnStatsCellRenderer,
} from "./cellRenderers";

type ReviewDetailsValueGetterParams = ValueGetterParams<ReviewDetailsFrontend>;

const numberToPercent = (value: number) => {
	return Math.round(value * 100);
};

export const columnTypes = {
	nestedName: {
		comparator: (
			valueA: ReviewDetailsFrontend["subject"] | ReviewDetailsFrontend["subjectGroup"],
			valueB: ReviewDetailsFrontend["subject"] | ReviewDetailsFrontend["subjectGroup"]
		) => {
			if (valueA.name && valueB.name) {
				return valueA.name.localeCompare(valueB.name);
			}
			if (valueA.name) {
				return -1;
			}
			if (valueB.name) {
				return 1;
			}
			return 0;
		},
		filterValueGetter: (params: ReviewDetailsValueGetterParams) => {
			if (!params.colDef.field) {
				return null;
			}
			const value = params.data?.[params.colDef.field] as ReviewDetailsFrontend["subject" | "subjectGroup"];
			return value?.name;
		},
	},
	turnStats: {
		//filter: "agNumberColumnFilter",
		comparator: (
			valueA: ReviewDetailsFrontend["authorTurnStats"] | ReviewDetailsFrontend["signerTurnStats"],
			valueB: ReviewDetailsFrontend["authorTurnStats"] | ReviewDetailsFrontend["signerTurnStats"]
		) => {
			const valueAAsRatio =
				valueA && "total" in valueA && "completed" in valueA && valueA.total > 0
					? valueA.completed / valueA.total
					: 0;
			const valueBAsRatio =
				valueB && "total" in valueB && "completed" in valueB && valueB.total > 0
					? valueB.completed / valueB.total
					: 0;

			return valueAAsRatio - valueBAsRatio;
		},
		cellRenderer: TurnStatsCellRenderer,
		filterValueGetter: (params: ReviewDetailsValueGetterParams) => {
			if (!params.colDef.field) {
				return null;
			}
			const value = params.data?.[params.colDef.field] as ReviewDetailsFrontend[
				| "authorTurnStats"
				| "signerTurnStats"];
			if (!value?.total || !("total" in value) || !("completed" in value)) {
				return 0;
			}
			return numberToPercent(value?.completed / value?.total);
		},
	},
	progressPercent: {
		//filter: "agNumberColumnFilter",
		cellRenderer: TotalProgressCellRenderer,
		valueGetter: (params: ReviewDetailsValueGetterParams) => {
			return numberToPercent(params.data?.totalProgressPercent ?? 0);
		},
		comparator: (
			valueA: number,
			valueB: number,
			nodeA: IRowNode<ReviewDetailsFrontend>,
			nodeB: IRowNode<ReviewDetailsFrontend>
		) => {
			const dataA = nodeA.data;
			const dataB = nodeB.data;

			const isClosedA = dataA?.closed === true;
			const isClosedB = dataB?.closed === true;
			const isCompletedA = dataA?.completed === true && !isClosedA;
			const isCompletedB = dataB?.completed === true && !isClosedB;

			// Helper to get sort value for a review
			// Closed: -1, Not completed: 1-100, Completed: 101-200
			const getSortValue = (isClosed: boolean, isCompleted: boolean, progress: number) => {
				if (isClosed) return -1;
				if (isCompleted) return 100 + progress;
				return progress + 1; // Not completed (in progress)
			};

			const sortValueA = getSortValue(isClosedA, isCompletedA, valueA);
			const sortValueB = getSortValue(isClosedB, isCompletedB, valueB);

			// Primary sort by status and progress
			const comparison = sortValueA - sortValueB;
			if (comparison !== 0) return comparison;

			// Stable sort by subject name when progress is equal
			const nameA = dataA?.subject?.name ?? "";
			const nameB = dataB?.subject?.name ?? "";
			return nameA.localeCompare(nameB);
		},
	},
};

export const getColumns = ({
	handleDeleteReview,
	showUnassigned,
	showExternal,
	showThreeSixty,
	showAwaitingApproval,
}: {
	handleDeleteReview: (reviewId: string) => void;
	showUnassigned: boolean;
	showExternal: boolean;
	showThreeSixty: boolean;
	showAwaitingApproval: boolean;
}): ColDef<ReviewDetailsFrontend>[] => {
	return [
		{
			headerName: "Action",
			cellRenderer: ActionCellRenderer,
			width: 132,
			minWidth: 132,
			pinned: "left",
		} as ColDef,
		{
			field: "subject" as const,
			headerName: "Subject",
			//filter: true,
			width: 166,
			sortable: true,
			type: "employee",
			pinned: "left",
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["subject"]>,
		{
			field: "adminsOnly" as const,
			headerName: "Admins Only",
			type: "lock",
			width: 16,
			pinned: "left",
			resizable: false,
			sortable: false,
			draggable: false,
			headerClass: "sr-only",
			cellRenderer: AdminsOnlyCellRenderer,
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["adminsOnly"]>,
		{
			field: "subjectGroup" as const,
			headerName: "Group",
			//filter: true,
			cellRenderer: SubjectGroupCellRenderer,
			resizable: true,
			sortable: true,
			flex: 1,
			type: "nestedName",
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["subjectGroup"]>,
		{
			field: "reviewDueDate" as const,
			headerName: "Due Date",
			//filter: "agDateColumnFilter",
			filterParams: {
				includeTime: true,
			},
			resizable: true,
			sortable: true,
			flex: 1,
			type: "date",
			cellRendererParams: { dateFormat: "MMM d, yyyy h:mm aaa" },
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["reviewDueDate"]>,
		{
			field: "authorTurnStats" as const,
			headerName: "Author Progress",
			type: "turnStats",
			resizable: true,
			sortable: true,
			flex: 1,
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["authorTurnStats"]>,
		{
			field: "signerTurnStats" as const,
			headerName: "Signer Progress",
			type: "turnStats",
			resizable: true,
			sortable: true,
			flex: 1,
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["signerTurnStats"]>,
		{
			field: "totalProgressPercent" as const,
			headerName: "Overall Progress",
			type: "progressPercent",
			resizable: true,
			sortable: true,
			flex: 1,
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["totalProgressPercent"]>,
		{
			field: "threeSixtyFormsCount" as const,
			headerName: "360 Forms",
			//filter: "agNumberColumnFilter",
			cellRenderer: FormCountCellRenderer,
			resizable: true,
			sortable: true,
			flex: 1,
			hide: !showThreeSixty,
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["threeSixtyFormsCount"]>,
		{
			field: "pendingThreeSixtyFormsCount" as const,
			headerName: "Pending 360 Forms",
			//filter: "agNumberColumnFilter",
			cellRenderer: FormCountWithAlertCellRenderer,
			resizable: true,
			sortable: true,
			flex: 1,
			hide: !showAwaitingApproval,
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["pendingThreeSixtyFormsCount"]>,
		{
			field: "unassignedFormsCount" as const,
			headerName: "Unassigned Forms",
			//filter: "agNumberColumnFilter",
			cellRenderer: FormCountWithAlertCellRenderer,
			resizable: true,
			sortable: true,
			flex: 1,
			hide: !showUnassigned,
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["unassignedFormsCount"]>,
		{
			field: "externalFormsCount" as const,
			headerName: "External Forms",
			//filter: "agNumberColumnFilter",
			cellRenderer: FormCountCellRenderer,
			resizable: false,
			sortable: true,
			flex: 1,
			hide: !showExternal,
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["externalFormsCount"]>,
		{
			field: "_id" as const,
			headerName: "",
			width: 56,
			pinned: "right",
			type: "dropdown",
			cellRendererParams: (rowData: ReviewDetailsFrontend) => ({
				options: [
					{
						key: "deleteReview",
						label: "Delete Review",
						onClick: () => handleDeleteReview(rowData?._id ?? ""),
					},
				],
			}),
		} as ColDef<ReviewDetailsFrontend, ReviewDetailsFrontend["_id"]>,
	];
};
